/**
 * Rate Limit Error Component
 *
 * Displays user-friendly rate limiting messages with countdown timer
 */

import React, { useState, useEffect } from 'react';
import { AlertCircle, Clock } from 'lucide-react';

const RateLimitError = ({ error, retryAfter: initialRetryAfter, onRetry }) => {
  const [retryAfter, setRetryAfter] = useState(initialRetryAfter || 60);
  const [canRetry, setCanRetry] = useState(false);

  useEffect(() => {
    if (retryAfter <= 0) {
      setCanRetry(true);
      return;
    }

    const timer = setInterval(() => {
      setRetryAfter((prev) => {
        if (prev <= 1) {
          setCanRetry(true);
          clearInterval(timer);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const formatTime = (seconds) => {
    if (seconds < 60) {
      return `${seconds} second${seconds !== 1 ? 's' : ''}`;
    }
    const minutes = Math.ceil(seconds / 60);
    return `${minutes} minute${minutes !== 1 ? 's' : ''}`;
  };

  return (
    <div className="rounded-md bg-yellow-50 border border-yellow-200 p-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <AlertCircle className="h-5 w-5 text-yellow-400" aria-hidden="true" />
        </div>
        <div className="ml-3 flex-1">
          <h3 className="text-sm font-medium text-yellow-800">
            Rate Limit Exceeded
          </h3>
          <div className="mt-2 text-sm text-yellow-700">
            <p>
              {error?.rateLimitMessage ||
                'Too many requests. Please try again later.'}
            </p>
            {!canRetry && retryAfter > 0 && (
              <div className="mt-3 flex items-center">
                <Clock className="h-4 w-4 mr-2" />
                <span className="font-medium">
                  Retry available in: {formatTime(retryAfter)}
                </span>
              </div>
            )}
          </div>
          {canRetry && onRetry && (
            <div className="mt-4">
              <button
                type="button"
                onClick={onRetry}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-yellow-700 bg-yellow-100 hover:bg-yellow-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
              >
                Try Again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default RateLimitError;
